import { tz } from '@date-fns/tz';
import { format, parseISO } from 'date-fns';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import type {
  FinancesBucketGranularity,
  FinancesRefundBreakdownBucket,
} from './apiHelpers';
import {
  FINANCES_CHART_HEIGHT,
  formatCompactCurrency,
  getBarHeight,
  getChartScale,
} from './chartUtils';
import styles from './RefundBreakdownChart.scss';

export interface RefundBreakdownChartProps {
  granularity?: FinancesBucketGranularity;
  buckets: FinancesRefundBreakdownBucket[];
}

// Buckets are returned as UTC instants (e.g. month start = `...T00:00:00.000Z`).
// Format in UTC so the label reflects that calendar period regardless of the
// browser's timezone — otherwise a browser behind UTC shifts every label back a
// period (e.g. Jan 1 00:00 UTC renders as "Dec 31").
const UTC = { in: tz('UTC') };

const formatBucketLabel = (
  bucketStart: string,
  granularity?: FinancesBucketGranularity,
): string => {
  const date = parseISO(bucketStart, UTC);

  if (granularity === 'month') {
    return format(date, 'MMM', UTC);
  }

  return format(date, 'MMM d', UTC);
};

const isEmptyBucket = (bucket: FinancesRefundBreakdownBucket): boolean =>
  bucket.canceled === 0 && bucket.other === 0;

// Year-to-date (and similar month ranges) fill the whole year, so the months
// after the current one come back empty. Drop those trailing $0 buckets so the
// chart ends at the latest month that has refunds. Daily ranges keep every
// bucket so short windows still show their full timeline.
const trimTrailingEmptyMonths = (
  buckets: FinancesRefundBreakdownBucket[],
  granularity?: FinancesBucketGranularity,
): FinancesRefundBreakdownBucket[] => {
  if (granularity !== 'month') {
    return buckets;
  }

  let end = buckets.length;
  while (end > 0 && isEmptyBucket(buckets[end - 1])) {
    end -= 1;
  }

  return end === 0 ? buckets : buckets.slice(0, end);
};

export const RefundBreakdownChart = ({
  granularity,
  buckets,
}: RefundBreakdownChartProps) => {
  const visibleBuckets = trimTrailingEmptyMonths(buckets, granularity);

  const scale = getChartScale(
    Math.max(
      ...visibleBuckets.map((bucket) => bucket.canceled + bucket.other),
      0,
    ),
  );

  // Tick value -> distance from the top of the plot as a percentage.
  const tickOffsetPercent = (value: number): number =>
    (1 - value / scale.max) * 100;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={cx(typography.h5, styles.title)}>Refund breakdown</h3>
        <div className={styles.legend}>
          <span className={cx(typography.c3, styles.legendItem)}>
            <span className={cx(styles.legendSwatch, styles.canceled)} />
            Canceled
          </span>
          <span className={cx(typography.c3, styles.legendItem)}>
            <span className={cx(styles.legendSwatch, styles.other)} />
            Other
          </span>
        </div>
      </header>

      <div className={styles.chart}>
        <div className={styles.yAxis}>
          {scale.ticks.map((tick) => (
            <span
              className={styles.yTick}
              key={tick}
              style={{ top: `${tickOffsetPercent(tick)}%` }}
            >
              {formatCompactCurrency(tick)}
            </span>
          ))}
        </div>

        <div className={styles.plotColumn}>
          <div className={styles.plotArea}>
            <div className={styles.gridLines} aria-hidden>
              {scale.ticks.map((tick, index) => (
                <div
                  className={styles.gridLine}
                  key={tick}
                  style={{
                    top: `${tickOffsetPercent(tick)}%`,
                    ...(index === 0 && {
                      borderTop: '1px solid var(--hairline)',
                    }),
                  }}
                />
              ))}
            </div>
            <div className={styles.barsRow}>
              {visibleBuckets.map((bucket) => {
                const canceledHeight = getBarHeight(
                  bucket.canceled,
                  scale.max,
                  FINANCES_CHART_HEIGHT,
                );
                const otherHeight = getBarHeight(
                  bucket.other,
                  scale.max,
                  FINANCES_CHART_HEIGHT,
                );
                const total = bucket.canceled + bucket.other;

                return (
                  <div className={styles.barGroup} key={bucket.bucketStart}>
                    <div className={styles.stackedBar}>
                      {total > 0 && (
                        <span className={styles.totalLabel}>
                          {formatCompactCurrency(total)}
                        </span>
                      )}
                      {otherHeight > 0 && (
                        <div
                          className={styles.otherBar}
                          style={{
                            height: `${otherHeight}px`,
                            ...(canceledHeight > 0 && { borderRadius: 0 }),
                          }}
                        />
                      )}
                      {canceledHeight > 0 && (
                        <div
                          className={styles.canceledBar}
                          style={{ height: `${canceledHeight}px` }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.xLabels}>
            {visibleBuckets.map((bucket) => (
              <span
                className={cx(typography.c3, styles.xLabel)}
                key={bucket.bucketStart}
              >
                {formatBucketLabel(bucket.bucketStart, granularity)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
